/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * V-4 (S620): fail the build rather than shipping a shop that sends its
 * customers to a different environment to sign in.
 *
 * lib/auth-redirect.ts falls back to https://deskshare.com when this variable is
 * unset, and that fallback is invisible when it is wrong: the customer clicks
 * Sign in, signs in successfully on PRODUCTION, and comes back to the local or
 * feature shop without their cart. Nothing errors, nothing is logged, and the
 * only symptom is a customer who "lost" their basket.
 *
 * The shop already applies this treatment to the refund policy address, where
 * validate_env fails a deployment rather than letting a customer meet the
 * problem.
 *
 * Two modes are exempt, and only these two:
 *  - "development": `vite dev` with no .env is a legitimate starting point, and
 *    nobody but the developer meets the fallback there.
 *  - "test": vitest runs with MODE="test" and no .env file at all. This guard
 *    exists to stop a BUILT BUNDLE reaching customers misconfigured; failing the
 *    test runner instead would only teach whoever hit it to delete the guard.
 */
import { deliveredSession } from "@/config/session";

const ENV_GUARD_EXEMPT_MODES = ["development", "test"];

if (
  !ENV_GUARD_EXEMPT_MODES.includes(import.meta.env.MODE) &&
  !import.meta.env.VITE_DESKSHARE_WEBSITE_URL
) {
  throw new Error(
    "VITE_DESKSHARE_WEBSITE_URL is not set. Set it to the Deskshare website " +
      "that serves /auth for THIS environment.",
  );
}

const configuration = {
  react_env: import.meta.env.VITE_REACT_ENV,
  // FCR S732: the delivered session number, or undefined (see config/session.ts).
  deskshare_session: deliveredSession(),
  domain: import.meta.env.VITE_DOMAIN,
  backend_url: import.meta.env.VITE_API_URL, // Fixed: Use VITE_API_URL (matches .env.example)
  // The Deskshare website origin, which serves /auth, sign-out and JWT minting.
  // The fallback chain means no environment has to change on the day the
  // cutover ships.
  deskshare_website_url: import.meta.env.VITE_DESKSHARE_WEBSITE_URL,
  auth_frontend_url: import.meta.env.VITE_AUTH_FRONTEND_URL,
  /*
   * Item 3 (FCR S687): the PUBLIC origin of the authentication service API --
   * the origin the browser calls for /api/auth/token/refresh, /exchange and
   * /revoke.
   *
   * There is deliberately NO fallback to deskshare_website_url. That variable is
   * documented in this shop's own .env.example as the marketing website -- the
   * React site customers browse, on port 4000 locally. It has never served
   * /api/auth/* and it never will; the authentication routes live in the
   * Deskshare Website's DJANGO backend, at a different origin. So a fallback
   * would not degrade gracefully: it would guarantee a 404 on every renewal, in
   * an environment that looks correctly configured.
   *
   * There is also deliberately NO build-time throw the way
   * VITE_DESKSHARE_WEBSITE_URL has one above. The shop must still serve a guest
   * checkout in an environment with no session feature configured, and a build
   * that refuses to start would make that situation worse rather than better.
   * The gate is validate_env (item N5), which requires this variable in staging
   * and production; authServiceBaseUrl() returns null here and the shop simply
   * does not have the feature, which is today's behaviour exactly.
   *
   * It is NOT necessarily the same value as the backend's AUTH_SERVICE_URL,
   * which is a server-to-server address and may be internal.
   */
  auth_service_url: import.meta.env.VITE_AUTH_SERVICE_URL,
  // EX-10 (S576): VITE_AUTH_LOGIN_PATH is deliberately NOT read any more. It is
  // set to /shop/ in the deployed environments, and the website has no /shop/
  // route, so reading it would send every customer to a page that does not
  // exist. The sign-in path is written in lib/auth-redirect.ts.
  frontend_url: import.meta.env.VITE_DOMAIN,
  stripe: {
    publishable_key: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY,
  },
  google_maps_api_key: import.meta.env.VITE_GOOGLE_MAP_API_KEY,
  sentry_dsn: import.meta.env.VITE_SENTRY_DSN,
};

export default configuration;
